import { useContext, useMemo } from 'react';
import { Card } from 'components';
import { DateRange } from 'components/DateRange';
import { useFormData } from 'domain/hooks';
import { Station } from 'domain/models/api.models';
import ObservationsList from 'pages/observations/ObservationsList';
import { useTranslation } from 'react-i18next';
import { AppRoutes } from 'domain/routes';
import { decimals } from 'domain/utils';
import { timeElapsedUTC, useBadge } from 'domain/utils/time.utils';
import { cn } from 'domain/core';
import { CardListContext, CARD_ITEM_CLASSNAME } from 'pages/base/CardList';

import { useObservations } from 'domain/hooks/api/observations';
import { useConvertData } from 'domain/hooks/useConvertData';
import { ObservationGraphics } from './ObservationGraphics';
import { LinkGuard, RoleGuard } from 'components/RoleGuard';
import { ALL_VARIABLES } from 'domain/utils/variables';

export const ObservationCard = ({ item }: { item: Station }) => {
	const { expanded, setExpanded } = useContext(CardListContext);

	const { t } = useTranslation(['labels']);

	const today = new Date();
	// Format date to YYYY-MM-DD
	const todayFormatted = today.toISOString().split('T')[0];
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 14); // 14 days ago
	const yesterdayFormatted = yesterday.toISOString().split('T')[0];

	const [date, setDate] = useFormData({
		startDate: yesterdayFormatted,
		endDate: todayFormatted,
	});

	const elapsed = timeElapsedUTC(item.lastUpdate + 'Z');
	const badge = useBadge(elapsed);

	const currentExpanded = useMemo(
		() => expanded === item.id,
		[expanded, item.id]
	);

	const [rawData, dataLoading] = useObservations(
		currentExpanded ? item.id : undefined,
		ALL_VARIABLES,
		undefined,
		undefined,
		date.startDate + 'T00:00:00.000000000Z',
		date.endDate + 'T23:59:59.000000000Z'
	);

	const [data] = useConvertData(rawData);

	if (!item) {
		return <></>;
	}

	return (
		<div className={cn('flex', CARD_ITEM_CLASSNAME + item.id)}>
			<Card
				className={cn(
					item.deleted ? 'border-0 opacity-50' : '',
					'mb-1',
					currentExpanded ? ' h-[calc(100vh_-_140px)]' : 'h-[100%] bg-white'
				)}>
				<Card.Header onClick={() => setExpanded(item.id)}>
					<Card.Badge status={badge}></Card.Badge>
					<Card.Titles>
						<Card.Title>{item.name}</Card.Title>
						<Card.Subtitle>
							{t(`labels:dates.ago.${elapsed[1]}`, { count: elapsed[0] })}
						</Card.Subtitle>
					</Card.Titles>
					<Card.Info>
						<Card.InfoItemLabel>
							{decimals(item.longitude, 3)}, {decimals(item.latitude, 3)}
						</Card.InfoItemLabel>
						<RoleGuard role='ROLE_ADMIN'>
							<Card.InfoItemLabel className='text-sm text-blue-500'>
								<LinkGuard
									role='ROLE_ADMIN'
									to={AppRoutes.EditStation.replace(':id', '' + item.id)}>
									{t('labels:edit')}
								</LinkGuard>
							</Card.InfoItemLabel>
						</RoleGuard>
					</Card.Info>
				</Card.Header>
				<Card.Body>
					<Card.KeyValue>
						<Card.KeyValueItem
							label={t('labels:station_registers')}
							value={
								<DateRange
									startDate={date.startDate}
									endDate={date.endDate}
									setEndDate={setDate('endDate')}
									setStartDate={setDate('startDate')}
								/>
							}
						/>
					</Card.KeyValue>
					{/* Gráficas */}
					{currentExpanded && (
						<ObservationGraphics data={data} loading={dataLoading} />
					)}
					{currentExpanded && <ObservationsList data={rawData} />}
				</Card.Body>
			</Card>
		</div>
	);
};
